import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { NetstaffLogo } from "@/components/NetstaffLogo";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { HERO_IMG, CARE_IMG, TECH_IMG } from "@/lib/constants";
import {
  Stethoscope, Heart, HandHeart, Building2, HomeIcon, Sparkles,
  ShieldCheck, Clock, MapPin, ChevronRight, Phone, Mail, ArrowRight, Activity
} from "lucide-react";

function resolveImg(url) {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${url}`;
}

const ICONS = [Stethoscope, HandHeart, Heart, HomeIcon, Building2, Activity];

const facilitySteps = [
  { n: "01", title: "Submit a request", desc: "Tell us what you need — position, shift, urgency." },
  { n: "02", title: "Receive a quote", desc: "Transparent pricing with markup, premiums, and HST." },
  { n: "03", title: "We find the talent", desc: "Smart-matched, vetted, credentialed professionals." },
  { n: "04", title: "Staff confirmed", desc: "Approved candidates assigned and notified." },
  { n: "05", title: "Shift completed", desc: "GPS check-in, timesheet generated automatically." },
  { n: "06", title: "Invoice settled", desc: "Approved timesheets become clean PDF invoices." },
];

const professionalSteps = [
  "Register and upload credentials",
  "Set your availability and rates",
  "Receive matched shift offers",
  "Accept with one tap, work the shift",
  "Check in/out, get paid on time",
];

export default function Landing() {
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => setServices([]));
  }, []);

  return (
    <div className="min-h-screen hero-gradient">
      {/* NAV */}
      <header className="glass-nav sticky top-0 z-50 border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="nav-home-link">
            <NetstaffLogo />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-700">
            <a href="#services" className="hover:text-brand-navy">Services</a>
            <a href="#how" className="hover:text-brand-navy">How it works</a>
            <a href="#contact" className="hover:text-brand-navy">Contact</a>
            <Link to="/login" className="hover:text-brand-navy" data-testid="nav-login-link">Sign in</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/request-staff">
              <Button className="bg-brand-navy hover:bg-brand-blue rounded-full" data-testid="nav-request-staff-btn">
                Request Staff
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-40" />
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center relative">
          <div className="md:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-sm text-slate-600 mb-6">
              <span className="w-2 h-2 rounded-full bg-brand-teal animate-pulse" />
              Trusted by Ontario healthcare facilities since 2020
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-navy leading-[1.05]" style={{ fontFamily: "Outfit, sans-serif" }}>
              Reliable Healthcare<br />
              Staffing When You<br />
              <span className="text-brand-teal">Need It Most.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Connecting healthcare facilities with qualified healthcare professionals across Ontario.
              From RNs to PSWs to Social Workers — staffed, credentialed, and on-shift.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/request-staff">
                <Button size="lg" className="bg-brand-navy hover:bg-brand-blue rounded-full px-8 h-12 text-base" data-testid="hero-request-staff-btn">
                  Request Healthcare Staff
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/join">
                <Button size="lg" variant="outline" className="rounded-full px-8 h-12 text-base border-2 border-brand-navy text-brand-navy hover:bg-slate-50" data-testid="hero-join-btn">
                  Join Our Healthcare Network
                </Button>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[
                { n: "850+", l: "Professionals" },
                { n: "12 min", l: "Avg. fill time" },
                { n: "98%", l: "Fill rate" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="text-2xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>{s.n}</div>
                  <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-5 relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-brand-navy/10 border border-white">
              <img src={HERO_IMG} alt="Healthcare professionals" className="w-full h-[520px] object-cover" />
              <div className="absolute inset-x-4 bottom-4 bg-white/95 backdrop-blur rounded-2xl p-4 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-teal/10 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-brand-teal" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900">All staff fully credentialed</div>
                    <div className="text-xs text-slate-500">CPR · BLS · VSS · License verified</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -top-6 -left-6 bg-white rounded-2xl p-4 shadow-lg border border-slate-100 hidden md:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-navy flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Next dispatch</div>
                  <div className="text-sm font-semibold text-brand-navy">Available now</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.18em] text-brand-teal font-semibold mb-3">Our services</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-brand-navy leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Workforce coverage across every clinical setting.
            </h2>
            <p className="mt-4 text-slate-600">
              We operate as your dedicated staffing dispatcher — not a job board. Every fill is matched, vetted, and managed by NetStaff.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const Icon = ICONS[i % ICONS.length];
              const img = resolveImg(s.image_url);
              return (
                <div
                  key={s.id || s.title}
                  className="group rounded-2xl bg-white border border-slate-200 hover:border-brand-teal hover:shadow-lg transition-all duration-300 animate-fade-up overflow-hidden"
                  style={{ animationDelay: `${i * 0.06}s` }}
                  data-testid={`service-card-${i}`}
                >
                  {img ? (
                    <div className="relative h-48 overflow-hidden">
                      <img src={img} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-transparent to-transparent" />
                      <div className="absolute top-4 left-4 w-11 h-11 rounded-xl bg-white/95 backdrop-blur flex items-center justify-center shadow-md">
                        <Icon className="w-5 h-5 text-brand-navy" />
                      </div>
                    </div>
                  ) : (
                    <div className="h-48 flex items-center justify-center bg-brand-navy/5">
                      <Icon className="w-14 h-14 text-brand-navy/30" />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{s.description}</p>
                  </div>
                </div>
              );
            })}
            {services.length === 0 && (
              <div className="col-span-full text-center py-10 text-slate-400">Services loading…</div>
            )}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <div className="text-xs uppercase tracking-[0.18em] text-brand-teal font-semibold mb-3">How it works</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-brand-navy leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                Six steps from request to paid invoice.
              </h2>
              <p className="mt-4 text-slate-600">
                Our dispatchers control every fill — you get the right professional, on time, every time.
              </p>
              <img src={CARE_IMG} alt="Caregiver" className="mt-10 rounded-3xl shadow-lg w-full object-cover h-72" />
            </div>
            <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
              {facilitySteps.map((s, i) => (
                <div
                  key={s.n}
                  className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-brand-navy transition-colors animate-fade-up"
                  style={{ animationDelay: `${i * 0.05}s` }}
                  data-testid={`facility-step-${i}`}
                >
                  <div className="text-3xl font-bold text-brand-teal" style={{ fontFamily: "Outfit, sans-serif" }}>{s.n}</div>
                  <div className="mt-3 font-semibold text-slate-900">{s.title}</div>
                  <div className="text-sm text-slate-600 mt-1">{s.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FOR PROFESSIONALS */}
      <section className="py-20 md:py-28 bg-brand-navy text-white relative overflow-hidden">
        <div className="absolute inset-0 dot-pattern opacity-10" />
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-12 gap-12 items-center relative">
          <div className="md:col-span-6 order-2 md:order-1">
            <img src={TECH_IMG} alt="Healthcare tech" className="rounded-3xl shadow-2xl w-full object-cover h-96" />
          </div>
          <div className="md:col-span-6 order-1 md:order-2">
            <div className="text-xs uppercase tracking-[0.18em] text-brand-teal font-semibold mb-3">For professionals</div>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Work shifts on your terms. <span className="text-brand-teal">Get paid faster.</span>
            </h2>
            <ul className="mt-8 space-y-4">
              {professionalSteps.map((step, i) => (
                <li key={step} className="flex items-start gap-3" data-testid={`pro-step-${i}`}>
                  <div className="w-8 h-8 rounded-full bg-brand-teal/20 border border-brand-teal flex items-center justify-center text-brand-teal font-semibold text-sm shrink-0">
                    {i + 1}
                  </div>
                  <span className="text-white/90 pt-1">{step}</span>
                </li>
              ))}
            </ul>
            <Link to="/join" className="inline-block mt-8">
              <Button size="lg" className="bg-brand-teal hover:bg-brand-teal/90 rounded-full px-8 h-12" data-testid="pro-join-btn">
                Join the NetStaff Network <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Sparkles className="w-10 h-10 text-brand-teal mx-auto" />
          <h2 className="mt-4 text-3xl sm:text-5xl font-bold text-brand-navy leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Need staff this week?
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
            Submit a staffing request now — our dispatchers will respond with a quote within hours.
          </p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/request-staff">
              <Button size="lg" className="bg-brand-navy hover:bg-brand-blue rounded-full px-8 h-12" data-testid="cta-request-btn">
                Request Healthcare Staff
              </Button>
            </Link>
            <Link to="/login">
              <Button size="lg" variant="outline" className="rounded-full px-8 h-12 border-2 border-brand-navy text-brand-navy" data-testid="cta-signin-btn">
                Sign in to your dashboard
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-50 border-t border-slate-200 py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid gap-10 md:grid-cols-4">
            <div>
              <NetstaffLogo />
              <p className="mt-4 text-sm text-slate-600 leading-6">
                Healthcare staffing solutions for facilities and professionals across Ontario.
              </p>
              <div className="mt-5 space-y-2 text-sm text-slate-500">
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> North York, Ontario, Canada</div>
                <div className="flex items-center gap-2"><Phone className="w-4 h-4" /> +1 (416) 555-0100</div>
                <div className="flex items-center gap-2"><Mail className="w-4 h-4" /> dispatch@netstaff.ca</div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-brand-navy">Services</h3>
              <div className="mt-4 space-y-3 text-sm text-slate-600">
                <a href="#services" className="block hover:text-brand-navy">Nursing Staffing</a>
                <a href="#services" className="block hover:text-brand-navy">PSW Staffing</a>
                <a href="#services" className="block hover:text-brand-navy">Social Worker Staffing</a>
                <a href="#services" className="block hover:text-brand-navy">Home Care Staffing</a>
                <a href="#services" className="block hover:text-brand-navy">Long-Term Care Staffing</a>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-brand-navy">Company</h3>
              <div className="mt-4 space-y-3 text-sm text-slate-600">
                <a href="#how" className="block hover:text-brand-navy">How It Works</a>
                <a href="#contact" className="block hover:text-brand-navy">Contact Us</a>
                <Link to="/request-staff" className="block hover:text-brand-navy">Request Staffing</Link>
                <Link to="/join" className="block hover:text-brand-navy">Join Our Team</Link>
                <Link to="/login" className="block hover:text-brand-navy">Client / Professional Login</Link>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-brand-navy">Legal</h3>
              <div className="mt-4 space-y-3 text-sm text-slate-600">
                <Link to="/privacy-policy" className="block hover:text-brand-navy">Privacy Policy</Link>
                <Link to="/terms-and-conditions" className="block hover:text-brand-navy">Terms & Conditions</Link>
                <Link to="/cookie-policy" className="block hover:text-brand-navy">Cookie Policy</Link>
                <Link to="/accessibility-statement" className="block hover:text-brand-navy">Accessibility Statement (AODA)</Link>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-xs text-slate-400">
              © {new Date().getFullYear()} NetStaff Healthcare Solutions. All rights reserved.
            </div>
            <div className="text-xs text-slate-400">
              Serving healthcare facilities across Ontario, Canada.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
